function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import { toggleMark } from 'prosemirror-commands';
import { NodeSelection } from 'prosemirror-state';
import { buildMenuItems } from 'prosemirror-example-setup';
import { Dropdown, MenuItem } from 'prosemirror-menu';
import { imageIcon } from './menu/image';
import { getDocFormat, DocFormatEnum, isInlineDocFormat, prettyPrintLabel } from './utils';
import { insertVariable, insertPillbox, markActive, menuBarBuilder, MenuItemEnum } from './menu/utils';
import { WithActiveAsLabelDropdown } from './menu/dropdown';
import { isPresent } from '@ember/utils';
function createMenuIcon(src) {
  const elm = document.createElement('img');
  elm.className = 'ProseMirror__menu-icon';
  elm.src = src;
  return elm;
}
function createMenuLabel(label) {
  const elm = document.createElement('span');
  elm.className = 'ProseMirror__menu-label';
  elm.textContent = label;
  return elm;
}

/*
  A class that drives the rendering of the menubar at the top of the editor
*/
export class Menu {
  constructor(args) {
    _defineProperty(this, "schema", void 0);
    _defineProperty(this, "excludeMenuItems", void 0);
    _defineProperty(this, "prompt", void 0);
    _defineProperty(this, "variables", void 0);
    _defineProperty(this, "extensions", void 0);
    _defineProperty(this, "menuItems", void 0);
    _defineProperty(this, "isInline", false);
    _defineProperty(this, "isNda", false);
    Object.assign(this, args);
    const format = getDocFormat(this.schema);
    this.isInline = isInlineDocFormat(format);
    this.isNda = format === DocFormatEnum.Nda;
    this.menuItems = buildMenuItems(this.schema);
  }
  get toggleStrong() {
    if (!this.isNda) return this.menuItems[MenuItemEnum.ToggleStrong];
    return new MenuItem({
      ...this.menuItems.toggleStrong.spec,
      title: 'Can only bold variables at the current time',
      enable(state) {
        return state.selection.node && state.selection.node.attrs.isVariable;
      }
    });
  }
  get insertImage() {
    if (this.isInline) return undefined;
    return new MenuItem({
      ...this.menuItems.insertImage.spec,
      icon: imageIcon,
      run: (state, _dispatch, view) => {
        const {
          from,
          to
        } = state.selection;
        let attrs = {
          alt: state.doc.textBetween(from, to, ' '),
          src: '',
          title: ''
        };
        if (state.selection instanceof NodeSelection && state.selection.node.type == this.schema.nodes.image) {
          attrs = state.selection.node.attrs;
        }
        const callback = () => {
          view.dispatch(view.state.tr.replaceSelectionWith(this.schema.nodes.image.createAndFill(this.prompt.attrs)));
          view.focus();
        };
        this.prompt.render('image', callback, attrs);
      }
    });
  }
  get toggleLink() {
    if (this.isInline) return undefined;
    const {
      toggleLink
    } = this.menuItems;
    return new MenuItem({
      ...toggleLink.spec,
      run: (state, dispatch, view) => {
        if (markActive(state, this.schema.marks.link)) {
          toggleMark(this.schema.marks.link)(state, dispatch);
          return true;
        }
        const callback = () => {
          toggleMark(this.schema.marks.link, this.prompt.attrs)(view.state, view.dispatch);
          view.focus();
        };
        this.prompt.render('link', callback, {
          href: 'https://',
          title: ''
        });
      }
    });
  }
  get textTypeMenu() {
    if (this.isInline) return undefined;
    return new WithActiveAsLabelDropdown([new MenuItem(Object.assign(this.menuItems.makeParagraph.spec, {
      label: 'Normal text'
    })), new MenuItem(Object.assign(this.menuItems.makeHead1.spec, {
      label: 'Heading 1',
      class: 'h1'
    })), new MenuItem(Object.assign(this.menuItems.makeHead2.spec, {
      label: 'Heading 2',
      class: 'h2'
    })), new MenuItem(Object.assign(this.menuItems.makeHead3.spec, {
      label: 'Heading 3',
      class: 'h3'
    })), new MenuItem(Object.assign(this.menuItems.makeHead4.spec, {
      label: 'Heading 4',
      class: 'h4'
    })), new MenuItem(Object.assign(this.menuItems.makeHead5.spec, {
      label: 'Heading 5',
      class: 'h5'
    })), new MenuItem(Object.assign(this.menuItems.makeHead6.spec, {
      label: 'Heading 6',
      class: 'h6'
    }))], {
      label: 'Type...'
    });
  }
  get variablesMenu() {
    return new Dropdown(this.variables.map(variable => {
      return new MenuItem({
        title: `Insert ${variable}`,
        label: prettyPrintLabel(variable),
        class: 'ProseMirror__variables-menu-item',
        enable: state => insertVariable(variable)(state),
        run: insertVariable(variable)
      });
    }), {
      label: 'Variables',
      class: 'ProseMirror__variables-menu'
    });
  }
  get extensionsMenu() {
    return new Dropdown(this.extensions.map(({
      label,
      token,
      icon
    }) => {
      return new MenuItem({
        title: `Insert ${label}`,
        class: 'ProseMirror__extensions-menu-item',
        enable: state => insertPillbox(label, token)(state),
        run: insertPillbox(label, token),
        render() {
          const elm = document.createElement('div');
          if (isPresent(icon)) {
            elm.append(createMenuIcon(icon));
          }
          elm.append(createMenuLabel(label));
          return elm;
        }
      });
    }), {
      label: 'Add dynamic content',
      class: 'ProseMirror__extensions-menu'
    });
  }
  get menu() {
    this.menuItems[MenuItemEnum.InsertImage] = this.insertImage;
    this.menuItems[MenuItemEnum.ToggleLink] = this.toggleLink;
    this.menuItems[MenuItemEnum.TextTypeMenu] = this.textTypeMenu;
    this.menuItems[MenuItemEnum.VariablesMenu] = this.variablesMenu;
    this.menuItems[MenuItemEnum.ToggleStrong] = this.toggleStrong;
    if (isPresent(this.extensions)) {
      this.menuItems[MenuItemEnum.ExtensionsMenu] = this.extensionsMenu;
    }
    return menuBarBuilder(this.menuItems, this.excludeMenuItems, this.variables);
  }
}